import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Copy, Download, MessageCircle, WalletCards } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

export default function QRTicketDialog({ open, onOpenChange, booking }) {
  if (!booking) return null;
  const ticketUnits = Array.isArray(booking.ticket_units) ? booking.ticket_units : [];
  const ticketSummary = booking.ticket_items?.length
    ? booking.ticket_items.map((item) => `${item.category} x ${item.quantity} (${ticketDateLabel(item, booking.date)})`).join(" / ")
    : `${booking.category} x ${booking.quantity || 1}`;
  const shareText = `Ticket for ${booking.item_title}\n${booking.venue}, ${booking.city}\n${booking.date} ${booking.time}\n${ticketSummary}`;
  const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(shareText)}`;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto p-0 border-0 bg-transparent shadow-none" data-testid="qr-ticket-dialog">
        <div className="ticket-stub bg-card border border-border rounded-2xl overflow-hidden">
          <div className="p-5 bg-primary/10">
            <p className="text-[10px] uppercase tracking-[0.3em] font-bold text-primary">e-Ticket</p>
            <h3 className="font-display text-2xl font-black mt-1">{booking.item_title}</h3>
            <p className="text-xs text-muted-foreground mt-1">{booking.venue} · {booking.city}</p>
            <p className="text-xs text-muted-foreground">{booking.date} · {booking.time}</p>
          </div>
          <div className="border-t border-dashed border-border" />
          <div className="p-5 grid place-items-center">
            {ticketUnits.length > 0 ? (
              <div className="w-full grid sm:grid-cols-2 gap-3">
                {ticketUnits.map((ticket) => (
                  <div key={ticket.ticket_id} className="rounded-2xl border border-border bg-background/70 p-4 text-center">
                    <div className="flex items-center justify-between gap-2 mb-3">
                      <p className="font-display font-black">Ticket {ticket.ticket_number}</p>
                      <span className={
                        ticket.status === "scanned"
                          ? "text-[10px] uppercase font-black text-destructive"
                          : "text-[10px] uppercase font-black text-primary"
                      }>
                        {ticket.status === "scanned" ? "Used" : "Ready"}
                      </span>
                    </div>
                    <div className="mb-3 rounded-xl border border-border bg-muted/40 p-2 text-left">
                      <p className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Ticket type</p>
                      <p className="text-sm font-black text-primary">{ticket.category || booking.category || "General"}</p>
                      <p className="mt-1 text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Valid day</p>
                      <p className="text-xs font-bold">{ticketDateLabel(ticket, booking.date)}</p>
                    </div>
                    <div className="bg-white p-3 rounded-xl inline-block">
                      <img src={ticket.qr_code} alt={`QR for ticket ${ticket.ticket_number}`} className="w-36 h-36" data-testid={`qr-image-${ticket.ticket_number}`} />
                    </div>
                    <p className="mt-3 font-mono text-[10px] text-muted-foreground break-all">{ticket.ticket_id}</p>
                    <div className="mt-3 flex justify-center gap-2">
                      <Button size="sm" variant="outline" className="h-8 rounded-full" onClick={() => copyTicket(ticket.ticket_id)}>
                        <Copy className="mr-1.5 h-3.5 w-3.5" /> Copy
                      </Button>
                      <Button size="sm" variant="outline" className="h-8 rounded-full" onClick={() => downloadQr(ticket.qr_code, `${ticket.ticket_id}.png`)}>
                        <Download className="mr-1.5 h-3.5 w-3.5" /> Save
                      </Button>
                      <Button size="sm" variant="outline" className="h-8 rounded-full" onClick={() => addToAppleWallet(booking.booking_id, ticket.ticket_id)}>
                        <WalletCards className="mr-1.5 h-3.5 w-3.5" /> Wallet
                      </Button>
                      <Button size="sm" variant="outline" className="h-8 rounded-full" onClick={() => addToGoogleWallet(booking.booking_id, ticket.ticket_id)}>
                        <WalletCards className="mr-1.5 h-3.5 w-3.5" /> Google
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <>
                <div className="bg-white p-3 rounded-xl">
                  <img src={booking.qr_code} alt="QR" className="w-48 h-48" data-testid="qr-image" />
                </div>
                <p className="mt-3 font-mono text-xs text-muted-foreground">{booking.booking_id}</p>
                <div className="mt-3 flex justify-center gap-2">
                  <Button size="sm" variant="outline" className="rounded-full" onClick={() => copyTicket(booking.booking_id)}>
                    <Copy className="mr-2 h-4 w-4" /> Copy code
                  </Button>
                  <Button size="sm" variant="outline" className="rounded-full" onClick={() => downloadQr(booking.qr_code, `${booking.booking_id}.png`)}>
                    <Download className="mr-2 h-4 w-4" /> Save QR
                  </Button>
                  <Button size="sm" variant="outline" className="rounded-full" onClick={() => addToAppleWallet(booking.booking_id, booking.booking_id)}>
                    <WalletCards className="mr-2 h-4 w-4" /> Add to Apple Wallet
                  </Button>
                  <Button size="sm" variant="outline" className="rounded-full" onClick={() => addToGoogleWallet(booking.booking_id, booking.booking_id)}>
                    <WalletCards className="mr-2 h-4 w-4" /> Add to Google Wallet
                  </Button>
                </div>
              </>
            )}
            <p className="mt-1 text-xs">
              {booking.seats?.length ? `Seats: ${booking.seats.join(", ")}` : ticketSummary}
            </p>
            <p className="mt-2 text-xs font-semibold">
              Status:{" "}
              <span className={
                booking.status === "scanned" ? "text-destructive" :
                booking.status === "cancelled" ? "text-muted-foreground" :
                "text-primary"
              }>{booking.status?.toUpperCase()}</span>
            </p>
            <Button asChild className="mt-4 rounded-full">
              <a href={whatsappUrl} target="_blank" rel="noreferrer">
                <MessageCircle className="mr-2 h-4 w-4" /> Share ticket details
              </a>
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ticketDateLabel(item, fallbackDate) {
  const dates = Array.isArray(item?.valid_dates) ? item.valid_dates.filter(Boolean) : [];
  if (item?.scope === "multi_day" || item?.scan_policy === "once_per_day") {
    return dates.length > 1
      ? `${formatShortDate(dates[0])} to ${formatShortDate(dates[dates.length - 1])}`
      : formatShortDate(dates[0] || fallbackDate);
  }
  return formatShortDate(dates[0] || fallbackDate);
}

function formatShortDate(value) {
  if (!value) return "Event day";
  const date = new Date(`${String(value).slice(0, 10)}T12:00:00`);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" });
}

async function copyTicket(value) {
  await navigator.clipboard.writeText(value);
  toast.success("Ticket code copied");
}

function downloadQr(dataUrl, filename) {
  const link = document.createElement("a");
  link.href = dataUrl;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
}

async function addToAppleWallet(bookingId, ticketId) {
  try {
    const { data } = await api.post(`/bookings/${bookingId}/wallet/${ticketId}/link`);
    if (!data?.url) throw new Error("No Wallet link returned");
    window.location.href = data.url;
  } catch (error) {
    toast.error(error.response?.data?.detail || "Apple Wallet pass is not ready yet.");
  }
}

async function addToGoogleWallet(bookingId, ticketId) {
  try {
    const { data } = await api.post(`/bookings/${bookingId}/google-wallet/${ticketId}/link`);
    if (!data?.url) throw new Error("No Google Wallet link returned");
    window.location.href = data.url;
  } catch (error) {
    toast.error(error.response?.data?.detail || "Google Wallet pass is not ready yet.");
  }
}
